/**
 * Preview Mode Banner — Phase 2.2.
 *
 * Persistent full-width banner rendered by AdminLayout whenever RBAC
 * preview mode is active. Provides an "Exit Preview" button to
 * immediately restore the real Super Admin UI.
 *
 * Server-side session is unchanged — this is UX-only.
 */
import React, { useState } from "react";
import { Eye, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useRbac } from "../lib/rbac";

export default function PreviewBanner() {
  const { isPreviewing, previewRoleName, previewRoleId, endPreview } = useRbac();
  const [busy, setBusy] = useState(false);

  if (!isPreviewing) return null;

  const exit = async () => {
    setBusy(true);
    try {
      await endPreview();
      toast.success("Exited preview mode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full bg-amber-100 border-b-2 border-amber-500 text-amber-900 px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-40"
      data-testid="preview-mode-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye size={18} weight="bold" className="shrink-0" />
        <div className="text-sm font-medium truncate">
          <span className="font-mono uppercase tracking-widest text-xs mr-2">
            Preview Mode:
          </span>
          Viewing the interface as{" "}
          <strong data-testid="preview-role-name">{previewRoleName || previewRoleId}</strong>.
          No changes can be made.
        </div>
      </div>
      <button
        onClick={exit}
        disabled={busy}
        className="gf-btn gf-btn-outline text-xs border-amber-600 text-amber-900 hover:bg-amber-200 shrink-0"
        data-testid="preview-exit-button"
      >
        <X size={12} weight="bold" />
        {busy ? "Exiting…" : "Exit Preview"}
      </button>
    </div>
  );
}
